// BillingDashboard.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Card, // Import Card
  CardBody, // Import CardBody
  useColorModeValue, // Import useColorModeValue
  VStack, // Import VStack
  Heading, // Import Heading
} from "@chakra-ui/react";
import { getActiveSubscription, getBillingHistory } from "../../services/firebaseService";
import { fetchPlanByStripePriceId } from "../../services/planService";
import {
  getTradeCount,
} from "../../services/firebaseService";
import { useTranslation } from "react-i18next";


const BillingDashboard = ({ userId }) => {
  const { t, i18n } = useTranslation();

  const [subscription, setSubscription] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [tradesUsed, setTradesUsed] = useState(0);
  const [planDetails, setPlanDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cardBgColor = useColorModeValue("white", "gray.700");
  const textColor = useColorModeValue("gray.700", "white");

  useEffect(() => {
    const fetchBillingData = async () => {
      try {
        setLoading(true);
        console.log("[BillingDashboard] Fetching billing data for user ID:", userId);

        // Fetch the user's active subscription
        const activeSubscription = await getActiveSubscription(userId);
        if (activeSubscription) {
          console.log("[BillingDashboard] Fetched active subscription:", activeSubscription);
          setSubscription(activeSubscription);

          // Fetch plan details using the plan ID
          if (activeSubscription.planId) {
            const planData = await fetchPlanByStripePriceId(activeSubscription.planId);
            console.log("[BillingDashboard] Fetched plan details:", planData);

            // Find the matching billing cycle
            const matchingCycle = planData.billingCycles.find(
              (cycle) => cycle.stripePriceId === activeSubscription.planId
            );

            setPlanDetails({
              name: planData.name,
              tradeLimit: matchingCycle?.tradeLimit ?? "N/A",
              cycle: matchingCycle?.cycle ?? "N/A",
            });
          } else {
            console.warn("[BillingDashboard] No plan ID found in active subscription.");
          }

          const tradesUsed = await getTradeCount(userId);
          setTradesUsed(tradesUsed);

        } else {
          console.warn("[BillingDashboard] No active subscription found for user.");
          setSubscription(null);
        }

        // Fetch the user's payment history
        const history = await getBillingHistory(userId);
        console.log("[BillingDashboard] Fetched billing history:", history);
        setBillingHistory(history);
      } catch (err) {
        console.error("[BillingDashboard] Error fetching billing data:", err);
        setError(t("billingdashboard.fetch_error"));
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchBillingData();
    } else {
      console.warn("[BillingDashboard] No user ID provided.");
      setError(t("billingdashboard.user_id_missing"));
      setLoading(false);
    }
  }, [userId]);

  if (loading) {
    return (
      <Box textAlign="center" mt={4}>
        <Spinner size="lg" />
        <Text mt={4}>{t("billingdashboard.loading_message")}</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" mt={4}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Card bg={cardBgColor} borderRadius="lg" shadow="md" p={3} m={8}> {/* Card for the dashboard */}
      <CardBody>
        <VStack spacing={4} align="start"> {/* Use VStack for consistent spacing */}
          <Heading size="md" color={textColor}>            
            {t("billingdashboard.billing_summary")}
          </Heading>

          {/* Subscription Summary */}
          <Box mb={6}>
            {subscription ? (
              <Box mt={2}>
                <Text>
                <strong>{t("billingdashboard.current_plan")}:</strong> {planDetails?.name || "N/A"}
                </Text>
                <Text>
                <strong>{t("billingdashboard.status")}:</strong> {subscription.status || "N/A"}
                </Text>
                <Text>
                <strong>{t("billingdashboard.trades_allowed")}:</strong>{" "}
                {planDetails?.tradeLimit === -1
                    ? t("billingdashboard.unlimited")
                    : planDetails?.tradeLimit || "N/A"}
                </Text>
                <Text>
                <strong>{t("billingdashboard.trades_used")}:</strong> {tradesUsed}
                </Text>
                <Text>
                <strong>{t("billingdashboard.billing_cycle")}:</strong> {planDetails?.cycle || "N/A"}
                </Text>
                <Text>
                <strong>{t("billingdashboard.next_billing_date")}:</strong>{" "}
                  {subscription.currentPeriodEnd
                    ? new Date(subscription.currentPeriodEnd * 1000).toLocaleDateString()
                    : t("billingdashboard.not_available")}
                </Text>
              </Box>
            ) : (
              <Text color="red.500">{t("billingdashboard.no_active_subscription")}</Text>
            )}
          </Box>

          {/* Billing History Table */}
          {/* <Box>
        <Text fontSize="lg" fontWeight="bold" mb={4}>
          Billing History
        </Text>
        {billingHistory.length > 0 ? (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th>Payment ID</Th>
                <Th>Amount</Th>
                <Th>Date</Th>
                <Th>Status</Th>
              </Tr>
            </Thead>
            <Tbody>
              {billingHistory.map((payment) => (
                <Tr key={payment.id}>
                  <Td>{payment.id}</Td>
                  <Td>${payment.amount}</Td>
                  <Td>
                    {payment.paymentDate
                      ? new Date(payment.paymentDate.seconds * 1000).toLocaleDateString()
                      : "N/A"}
                  </Td>
                  <Td>{payment.status}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <Text>No billing history available.</Text>
        )}
      </Box> */}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default BillingDashboard;

